// ===============================
// CancellationDecisionPanel.tsx
// Simplified Inline Version
// ===============================

import React, { useState } from "react";
import { RequestsService } from "../../../../api";

type PaymentInfo = {
  status: string;
  totalPaid: number;
  totalPayable: number;
  payments?: any[];
};

type DecisionProps = {
  requestId: number;
  adminId: number;
  cancellationReason?: string;
  paymentInfo: PaymentInfo;
  onActionComplete?: () => void;
};

const truncate = (text: string, max: number) =>
  text.length > max ? text.slice(0, max) : text;

const CancellationDecisionPanel: React.FC<DecisionProps> = ({
  requestId,
  cancellationReason,
  paymentInfo,
  onActionComplete,
}) => {
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    try {
      setLoading(true);

      await RequestsService.updateServiceRequestStatusServiceRequestsRequestIdStatusPut(
        requestId,
        {
          main_status: "CANCELLATION",
          sub_status: "Cancellation Accepted",
          notes: truncate(remarks || "Cancellation approved", 150),
        }
      );

      alert("Cancellation approved");
      onActionComplete?.();
    } catch (err) {
      console.error(err);
      alert("Failed to approve cancellation");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!remarks.trim()) {
      alert("Rejection reason is required");
      return;
    }

    try {
      setLoading(true);

      if (paymentInfo?.status === "PAID") {
        await RequestsService.updateServiceRequestStatusServiceRequestsRequestIdStatusPut(
          requestId,
          {
            main_status: "IN-PROGRESS",
            sub_status: "Scheduling WIP",
            notes: truncate(`Cancellation rejected: ${remarks}`, 150),
          }
        );
      } else {
        await RequestsService.updateServiceRequestStatusServiceRequestsRequestIdStatusPut(
          requestId,
          {
            main_status: "NEW",
            sub_status: "Payment Pending",
            notes: truncate(`Cancellation rejected: ${remarks}`, 150),
          }
        );
      }

      alert("Cancellation rejected");
      onActionComplete?.();
    } catch (err) {
      console.error(err);
      alert("Failed to reject cancellation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded-xl p-3 bg-white shadow-sm">
      <h3 className="text-lg font-semibold">Service Request Cancel Processing</h3>
      <div className="grid grid-cols-12 gap-2 items-center">
        {/* Cancellation Reason */}
        <div className="col-span-4">
          <input
            type="text"
            readOnly
            value={truncate(cancellationReason || "No reason provided", 150)}
            className="w-full border rounded-md px-3 py-2 bg-gray-50 text-sm"
          />
        </div>

        {/* Remarks */}
        <div className="col-span-4">
          <input
            type="text"
            maxLength={150}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Rejection Reason"
            className="w-full border rounded-md px-3 py-2 text-sm"
          />
        </div>

        {/* Buttons */}
        <div className="col-span-4 flex gap-2">
          <button
            disabled={loading}
            onClick={handleApprove}
            className="flex-1 bg-green-600 text-white rounded-md py-2 text-sm hover:bg-green-700"
          >
            Approve
          </button>

          <button
            disabled={loading}
            onClick={handleReject}
            className="flex-1 bg-red-600 text-white rounded-md py-2 text-sm hover:bg-red-700"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancellationDecisionPanel;